import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Shield, ChevronRight, Mail, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating profile-avatar button + dropdown menu.
 *
 * Renders a small avatar pinned to the top-right corner. Click opens a
 * compact menu with:
 *   - Admin Panel (admins only)
 *   - Logout
 *
 * Visible on every screen size — solves the "I can't sign out on mobile"
 * problem (the Sidebar is `hidden lg:flex`, so on phones / narrow windows
 * the only Logout option used to be invisible).
 *
 * The desktop Sidebar still has its own Logout button. This is additive,
 * not a replacement.
 */
export default function ProfileMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inbox, setInbox] = useState({ messages: [], unread_count: 0 });
  const wrapperRef = useRef(null);

  const loadInbox = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/profile/inbox`, { withCredentials: true });
      setInbox(r.data);
    } catch (_e) { /* silent — inbox is non-critical */ }
  }, [user]);

  // Refresh inbox on mount + every 60s while authenticated.
  useEffect(() => {
    if (!user) return undefined;
    loadInbox();
    const t = setInterval(loadInbox, 60000);
    return () => clearInterval(t);
  }, [user, loadInbox]);

  // Mark a message read locally + on server
  const markRead = async (messageId) => {
    setInbox((prev) => ({
      ...prev,
      messages: prev.messages.map((m) => m.message_id === messageId ? { ...m, read_at: new Date().toISOString() } : m),
      unread_count: Math.max(0, prev.unread_count - 1),
    }));
    try { await axios.post(`${BACKEND_URL}/api/profile/inbox/${messageId}/read`, {}, { withCredentials: true }); } catch (_e) {}
  };

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!user) return null;

  return (
    <div
      ref={wrapperRef}
      className="fixed top-3 right-3 z-50"
      data-testid="profile-menu"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="profile-menu-trigger"
        aria-label="Open profile menu"
        className="relative block w-11 h-11 rounded-full overflow-hidden border-2 border-white/20 hover:border-[#FFCC00] bg-black/60 backdrop-blur-md transition-colors"
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[#FF3B30] flex items-center justify-center text-black font-bold text-base">
            {user.name?.[0]?.toUpperCase() ?? "R"}
          </div>
        )}
        {user.is_admin && (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#FFCC00] border-2 border-black flex items-center justify-center"
            title="Admin"
          >
            <Shield className="w-2 h-2 text-black" strokeWidth={3} />
          </span>
        )}
        {inbox.unread_count > 0 && !user.is_admin && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#FF3B30] border-2 border-black px-1 text-[10px] font-bold text-white flex items-center justify-center"
            title={`${inbox.unread_count} unread`}
            data-testid="profile-menu-unread-badge"
          >
            {inbox.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute top-12 right-0 w-56 bg-black/95 backdrop-blur-xl border border-white/15 shadow-2xl"
          data-testid="profile-menu-dropdown"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <div className="text-sm font-medium truncate">{user.name}</div>
            <div className="text-[11px] text-white/40 truncate">{user.email}</div>
          </div>
          {user.is_admin && (
            <button
              onClick={() => { setOpen(false); navigate("/admin"); }}
              data-testid="profile-menu-admin"
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-[#FFCC00] hover:bg-[#FFCC00]/10 transition-colors text-sm tracking-wider uppercase font-heading"
            >
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Admin Panel
              </span>
              <ChevronRight className="w-3.5 h-3.5 opacity-50" />
            </button>
          )}
          <button
            onClick={() => { setOpen(false); setInboxOpen(true); }}
            data-testid="profile-menu-inbox"
            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-white/80 hover:bg-white/5 hover:text-white transition-colors text-sm tracking-wider uppercase font-heading"
          >
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Inbox
            </span>
            <span className="flex items-center gap-2">
              {inbox.unread_count > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] bg-[#FF3B30] text-white">
                  {inbox.unread_count}
                </span>
              )}
              <ChevronRight className="w-3.5 h-3.5 opacity-50" />
            </span>
          </button>
          <button
            onClick={() => { setOpen(false); navigate("/garage"); }}
            data-testid="profile-menu-garage"
            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-white/80 hover:bg-white/5 hover:text-white transition-colors text-sm tracking-wider uppercase font-heading"
          >
            <span>Garage</span>
            <ChevronRight className="w-3.5 h-3.5 opacity-50" />
          </button>
          <button
            onClick={() => { setOpen(false); logout(); }}
            data-testid="profile-menu-logout"
            className="w-full flex items-center gap-3 px-4 py-3 text-left text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors text-sm tracking-wider uppercase font-heading border-t border-white/10"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      )}

      {/* Inbox sheet — overlays the page when opened from the dropdown */}
      {inboxOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center pt-12 px-4 bg-black/60 backdrop-blur-sm"
          data-testid="profile-menu-inbox-sheet"
          onClick={(e) => { if (e.target === e.currentTarget) setInboxOpen(false); }}
        >
          <div className="w-full max-w-xl bg-black/95 border border-white/15 max-h-[85vh] flex flex-col">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
              <Mail className="w-4 h-4 text-[#FFCC00]" />
              <h2 className="font-heading text-lg uppercase tracking-tight flex-1">Inbox</h2>
              <button
                onClick={() => setInboxOpen(false)}
                data-testid="profile-menu-inbox-close"
                className="p-1.5 hover:bg-white/5 transition-colors"
                aria-label="Close inbox"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {inbox.messages.length === 0 ? (
              <div className="p-12 text-center text-white/40 text-sm" data-testid="profile-menu-inbox-empty">
                No messages yet. Admins will use this to reach you for important notes.
              </div>
            ) : (
              <div className="overflow-y-auto divide-y divide-white/5">
                {inbox.messages.map((m) => {
                  const unread = !m.read_at;
                  return (
                    <div
                      key={m.message_id}
                      onClick={() => { if (unread) markRead(m.message_id); }}
                      data-testid={`profile-menu-inbox-message-${m.message_id}`}
                      className={`p-4 cursor-pointer transition-colors ${unread ? "bg-[#FFCC00]/5 hover:bg-[#FFCC00]/10" : "hover:bg-white/5"}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 w-2 h-2 rounded-full ${unread ? "bg-[#FFCC00]" : "bg-white/15"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-1">
                            <div className="font-heading text-sm uppercase tracking-tight truncate">{m.subject}</div>
                            <div className="text-[10px] text-white/40 ml-auto">
                              {new Date(m.sent_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-[11px] text-white/40 mb-2">From {m.from_admin_name}</div>
                          <div className="text-sm text-white/80 whitespace-pre-wrap">{m.body}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
